import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useRouter } from 'next/router'

import { HeaderProps } from '../../components/Header'
import { useOnClickOutside } from '../../hooks'
import { FooterProps } from '../../components/Footer'

import * as SC from './styled'

export type MainLayoutProps = {
  className?: string
  ids?: {
    top?: string
    content?: string
  }
  headerProps?: HeaderProps
  children?: React.ReactNode
  footerProps?: FooterProps
}

const MainLayout: FC<MainLayoutProps> = ({
  className,
  ids = {
    top: 'Top',
    content: 'Content',
  },
  headerProps,
  footerProps,
  children,
}) => {
  const [open, setOpen] = useState(false)
  const router = useRouter()
  const openTimeout = useRef<any>(null)

  const toggleMenu = useCallback(() => {
    if (!openTimeout.current) {
      setOpen(!open)
    }
  }, [open])

  // burger props
  const burgerProps = useMemo(
    () => ({
      ...headerProps?.burgerProps,
      'aria-expanded': open,
      onPress: toggleMenu,
    }),
    [headerProps?.burgerProps, open, toggleMenu]
  )

  // menu props
  const menuProps = {
    ...headerProps?.menuProps,
    open,
  }

  // menu ref
  const refMenu = useRef<HTMLDivElement>(null)

  // close menu on "escape"
  useEffect(() => {
    if (open === false) return
    const handlePressEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', handlePressEscape)
    return () => {
      window.removeEventListener('keydown', handlePressEscape)
    }
  }, [open])

  // close menu on click outside
  useOnClickOutside(refMenu, () => {
    // console.log('is clicking outside')
    if (open !== false) {
      setOpen(false)

      openTimeout.current = setTimeout(() => {
        if (openTimeout.current) {
          clearTimeout(openTimeout.current)
          openTimeout.current = null
        }
      }, 200)
    }
  })

  // close when navigating
  useEffect(() => {
    const handleRouteChange = () => {
      setOpen(false)
    }

    router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events])

  return (
    <>
      <SC.Container className={className}>
        {headerProps && (
          <SC._Header
            {...headerProps}
            burgerProps={burgerProps}
            menuProps={menuProps}
            refMenu={refMenu}
            id={ids.top}
          />
        )}
        <SC.Content id={ids.content}>{children}</SC.Content>
        {footerProps && <SC._Footer {...footerProps} />}
      </SC.Container>
    </>
  )
}

export default MainLayout
